import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minute timeout for file uploads
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method.toUpperCase()} request to ${config.url}`);
    
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle session expiration: redirect to login on 401
    if (error.response?.status === 401 && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login?expired=true';
    }

    return Promise.reject(error);
  }
);

// API functions
export const fileRequestApi = {
  // Create a new file request
  create: async (requestData) => {
    const formData = new FormData();

    console.log('Request data being sent:', requestData);
    console.log('cc_emails value:', requestData.cc_emails);
    console.log('cc_emails type:', typeof requestData.cc_emails);
    console.log('cc_emails is array?', Array.isArray(requestData.cc_emails));

    Object.keys(requestData).forEach(key => {
      if (key === 'cc_emails' && Array.isArray(requestData[key]) && requestData[key].length > 0) {
        const ccEmailsJson = JSON.stringify(requestData[key]);
        console.log('Adding cc_emails to FormData:', ccEmailsJson);
        formData.append(key, ccEmailsJson);
      } else if (key === 'is_reusable') {
        // Explicitly convert boolean to string for FormData
        formData.append(key, requestData[key] ? 'true' : 'false');
      } else if (key !== 'cc_emails') {
        formData.append(key, requestData[key]);
      } else {
        console.log('Skipping cc_emails - empty or invalid');
      }
    });

    const response = await api.post('/api/file-requests', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Toggle reusable status on a file request
  toggleReusable: async (requestId, isReusable) => {
    const formData = new FormData();
    formData.append('is_reusable', isReusable ? 'true' : 'false');

    const response = await api.patch(`/api/file-requests/${requestId}/reusable`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Extend expiration of a file request
  extendExpiration: async (requestId, extendHours) => {
    const formData = new FormData();
    formData.append('extend_hours', extendHours.toString());

    const response = await api.patch(`/api/file-requests/${requestId}/extend`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get user's file requests
  getByUser: async (createdBy) => {
    const response = await api.get(`/api/file-requests?created_by=${encodeURIComponent(createdBy)}`);
    return response.data;
  },

  // Get file request by token (for upload page)
  getByToken: async (token) => {
    const response = await api.get(`/api/file-requests/${token}`);
    return response.data;
  },

  // Get files for a request
  getFiles: async (requestId) => {
    const response = await api.get(`/api/file-requests/${requestId}/files`);
    return response.data;
  },
};

export const uploadApi = {
  // Upload files
  upload: async (token, files, onProgress) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await api.post(`/api/upload/${token}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  },

  // Download file
  download: async (fileId) => {
    const response = await api.get(`/api/download/${fileId}`, {
      responseType: 'blob',
    });
    return response;
  },
};

export const healthApi = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Authentication API
export const authApi = {
  // Login user
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    return response.data;
  },

  // Get current user info
  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  // Logout (client-side only for now)
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Get current user from localStorage
  getCurrentUserLocal: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Forgot password (public, no auth)
  forgotPassword: async (email) => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },
};

// Admin API
export const adminApi = {
  // Create new user
  createUser: async (userData) => {
    const response = await api.post('/api/admin/users', userData);
    return response.data;
  },

  // Get all users
  getAllUsers: async () => {
    const response = await api.get('/api/admin/users');
    return response.data;
  },

  // Get all file requests from all users
  getAllFileRequests: async () => {
    const response = await api.get('/api/admin/file-requests');
    return response.data;
  },

  // Update user role
  updateUserRole: async (userId, role) => {
    const response = await api.patch(`/api/admin/users/${userId}/role`, { role });
    return response.data;
  },

  // Reset user password (sends new setup email)
  resetUserPassword: async (userId) => {
    const response = await api.post(`/api/admin/users/${userId}/reset-password`);
    return response.data;
  },

  // Update user active status (suspend/unsuspend)
  updateUserStatus: async (userId, isActive) => {
    const response = await api.patch(`/api/admin/users/${userId}/status`, { is_active: isActive });
    return response.data;
  },

  // Delete a user
  deleteUser: async (userId) => {
    const response = await api.delete(`/api/admin/users/${userId}`);
    return response.data;
  },
};

// API Token management
export const apiTokenApi = {
  create: async (tokenData) => {
    const response = await api.post('/api/admin/api-tokens', tokenData);
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/api/admin/api-tokens');
    return response.data;
  },

  revoke: async (tokenId) => {
    const response = await api.delete(`/api/admin/api-tokens/${tokenId}`);
    return response.data;
  },

  update: async (tokenId, updateData) => {
    const response = await api.patch(`/api/admin/api-tokens/${tokenId}`, updateData);
    return response.data;
  },
};

// External API (using API tokens, not JWT)
export const externalApi = {
  createRequest: async (tokenValue, requestData) => {
    const response = await axios.post(`${API_BASE_URL}/api/v1/request`, requestData, {
      headers: {
        'Authorization': `Bearer ${tokenValue}`,
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });
    return response.data;
  },

  listRequests: async (tokenValue) => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/requests`, {
      headers: { 'Authorization': `Bearer ${tokenValue}` },
      timeout: 30000,
    });
    return response.data;
  },

  listFiles: async (tokenValue, requestId) => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/requests/${requestId}/files`, {
      headers: { 'Authorization': `Bearer ${tokenValue}` },
      timeout: 30000,
    });
    return response.data;
  },

  downloadFile: async (tokenValue, fileId) => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/download/${fileId}`, {
      headers: { 'Authorization': `Bearer ${tokenValue}` },
      responseType: 'blob',
      timeout: 600000,
    });
    return response;
  },
};

// Embed config API
export const embedApi = {
  getConfig: async (accessToken) => {
    const response = await api.get(`/api/embed-config/${accessToken}`);
    return response.data;
  },
};

export default api;
